import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { NAV } from "../../data/content";
import { scrollToId, scrollToTop } from "../../lib/scroll";

const scrollTo = (href) => scrollToId(href);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openPoetry, setOpenPoetry] = useState(false);
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    setMobile(false);
    scrollTo(href);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-[100] transition-colors duration-500 ${
        scrolled
          ? "bg-[#f4f2ec]/85 backdrop-blur-xl border-b border-[var(--line)]"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 md:px-10">
        <button
          data-testid="nav-logo"
          onClick={() => scrollToTop()}
          className="flex flex-col items-start"
        >
          <span className="font-display text-2xl tracking-tight text-[#1c2b4a]">
            Adonica<span className="text-[#9c7a33]"> </span>Shaw
          </span>
        </button>

        <nav className="hidden items-center gap-9 md:flex">
          <div
            className="relative"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
          >
            <button
              data-testid="nav-work-trigger"
              onClick={() => setOpen((v) => !v)}
              className="flex items-center gap-1.5 text-sm text-[#1c2b4a]/90 transition-colors hover:text-[#9c7a33]"
            >
              Work
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-300 ${
                  open ? "rotate-180" : ""
                }`}
              />
            </button>
            <AnimatePresence>
              {open && (
                <motion.div
                  data-testid="nav-work-dropdown"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.25, ease: [0.19, 1, 0.22, 1] }}
                  className="absolute left-0 top-full w-72 pt-4"
                >
                  <div className="overflow-hidden rounded-xl border border-[var(--line)] bg-[#ffffff]/95 backdrop-blur-xl">
                    {NAV.work.map((item) => (
                      <button
                        key={item.href}
                        data-testid={`nav-dropdown-${item.label.toLowerCase()}`}
                        onClick={() => go(item.href)}
                        className="group flex w-full flex-col items-start gap-0.5 border-b border-[var(--line)] px-5 py-4 text-left transition-colors last:border-0 hover:bg-[#9c7a33]/10"
                      >
                        <span className="font-display text-lg text-[#1c2b4a] transition-colors group-hover:text-[#9c7a33]">
                          {item.label}
                        </span>
                        <span className="text-xs text-[#5c6474]">{item.desc}</span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div
            className="relative"
            onMouseEnter={() => setOpenPoetry(true)}
            onMouseLeave={() => setOpenPoetry(false)}
          >
            <button
              data-testid="nav-poetry-trigger"
              onClick={() => go("#poetry")}
              className="flex items-center gap-1.5 text-sm text-[#1c2b4a]/90 transition-colors hover:text-[#9c7a33]"
            >
              Poetry
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-300 ${
                  openPoetry ? "rotate-180" : ""
                }`}
              />
            </button>
            <AnimatePresence>
              {openPoetry && (
                <motion.div
                  data-testid="nav-poetry-dropdown"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.25, ease: [0.19, 1, 0.22, 1] }}
                  className="absolute left-0 top-full w-72 pt-4"
                >
                  <div className="overflow-hidden rounded-xl border border-[var(--line)] bg-[#ffffff]/95 backdrop-blur-xl">
                    {NAV.poetry.map((item) => (
                      <a
                        key={item.href}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`nav-poetry-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => setOpenPoetry(false)}
                        className="group flex w-full flex-col items-start gap-0.5 border-b border-[var(--line)] px-5 py-4 text-left transition-colors last:border-0 hover:bg-[#9c7a33]/10"
                      >
                        <span className="font-display text-lg text-[#1c2b4a] transition-colors group-hover:text-[#9c7a33]">
                          {item.label}
                        </span>
                        <span className="text-xs text-[#5c6474]">{item.desc}</span>
                      </a>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {NAV.links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className="link-underline text-sm text-[#1c2b4a]/90 transition-colors hover:text-[#1c2b4a]"
            >
              {l.label}
            </button>
          ))}

          <button
            data-testid="nav-book-cta"
            onClick={() => go("#booking")}
            className="rounded-full bg-[#9c7a33] px-6 py-2.5 text-sm text-[#f4f2ec] transition-colors duration-300 hover:bg-[#7e6128]"
          >
            Book Adonica
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setMobile((v) => !v)}
          className="flex flex-col gap-1.5 md:hidden"
          aria-label="Menu"
        >
          <span className="h-px w-7 bg-[#1c2b4a]" />
          <span className="h-px w-7 bg-[#1c2b4a]" />
        </button>
      </div>

      <AnimatePresence>
        {mobile && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-[var(--line)] bg-[#f4f2ec]/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col px-6 py-4">
              {[...NAV.work, ...NAV.links].map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                  onClick={() => go(l.href)}
                  className="border-b border-[var(--line)] py-4 text-left font-display text-xl text-[#1c2b4a]"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-poetry"
                onClick={() => go("#poetry")}
                className="border-b border-[var(--line)] py-4 text-left font-display text-xl text-[#1c2b4a]"
              >
                Poetry
              </button>
              {NAV.poetry.map((p) => (
                <a
                  key={p.href}
                  href={p.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`nav-mobile-poetry-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setMobile(false)}
                  className="border-b border-[var(--line)] py-3 pl-4 text-left text-sm text-[#5c6474]"
                >
                  {p.label}
                </a>
              ))}
              <button
                data-testid="nav-mobile-book"
                onClick={() => go("#booking")}
                className="mt-5 rounded-full bg-[#9c7a33] py-3 text-[#f4f2ec]"
              >
                Book Adonica
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
